import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { debug } from '@/utils/debug';
import { validateCampaignName } from '@/utils/campaignNameValidation';
import type { Campaign } from '@/types/campaign';
import type { AppConfiguration, AppCampaignConfig } from '@/types/api';
import { meaningfulStatus } from '@/utils/formatEnum';
import { useAccountSettingsStore } from '@/store/accountSettingsStore';

export interface BudgetOptimizationConfig {
  hasConversionTracking: boolean;
  hasRevenuePerConversionTracking: boolean;
  revenuePerImpression: number | null;
  revenuePerConversion: number | null;
  dowWise: boolean;
  // Dev-only toggle for whether the optimization run mutates Google Ads.
  // Session-only. The previous account-level simplex knobs
  // (targetAbsDensityChange, minDensity) graduated to accountSettingsStore
  // when they were persisted.
  devImplementOptBudgets: boolean;
}

export interface CampaignBudgetSettings {
  campaignId: string;
  isOptimizationEnabled: boolean;
  originalOptimizationEnabled: boolean; // Original value from server - used to detect changes
  minimumAllocation: number; // percentage 0-100
  manualBudget: number | null;
  currentBudget: number;
  manualName: string | null;
  currentName: string;
  googleAdsAccountId: string | null;
  nameValidationError: string | null; // Validation error for campaign name
  campaignStatus: string; // Campaign status from Google Ads (ENABLED, PAUSED, etc.)
  // Server baseline at last init / hydrate. Mirrors
  // `originalOptimizationEnabled`. Discard reverts `campaignStatus`
  // to this so a pause/enable that the user later discards doesn't
  // leave the store stuck on the optimistic value (which would then
  // exclude the campaign from `getOptimizedCampaigns`).
  originalCampaignStatus: string;
}

export interface BudgetAllocationResult {
  campaignId: string;
  currentBudget: number;
  optimizedBudget: number;
  allocationPercentage: number;
  change: number;
  changePercentage: number;
}

interface BudgetOptimizerStore {
  // Configuration
  config: BudgetOptimizationConfig;
  campaignSettings: Map<string, CampaignBudgetSettings>;

  // UI State
  isOptimizing: boolean;
  optimizationResults: BudgetAllocationResult[] | null;
  error: string | null;
  manualBudgetChanges: Set<string>;
  manualNameChanges: Set<string>;
  optimizationToggleChanges: Set<string>; // Tracks campaigns where flouri_optimize_budget was toggled

  // Actions
  setConfig: (config: Partial<BudgetOptimizationConfig>) => void;
  setCampaignSettings: (campaignId: string, settings: Partial<CampaignBudgetSettings>) => void;
  toggleCampaignOptimization: (campaignId: string, enabled: boolean) => void;
  setManualBudget: (campaignId: string, budget: number) => void;
  setManualName: (campaignId: string, name: string) => void;
  setMinimumAllocation: (campaignId: string, percentage: number) => void;
  initializeCampaignSettings: (campaigns: Campaign[]) => void;
  setOptimizationResults: (results: BudgetAllocationResult[] | null) => void;
  setOptimizing: (isOptimizing: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  discardChanges: () => void;

  // Slice-provider surface for appConfigService. The Google store owns the
  // Google portion of campaign_list. preSave fires Google-specific dedicated
  // endpoints (updateManualBudgets / updateManualNames) before the app_config
  // POST — Meta has no equivalent endpoints so its store has no preSave.
  // Interface shape is identical across platform stores so adding a third
  // platform is just registering another slice provider.
  toConfigSlice: (existing?: AppConfiguration['configuration'] | null) => Partial<AppConfiguration['configuration']>;
  hydrateFromConfig: (config: AppConfiguration['configuration'] | undefined | null) => void;
  markSaved: () => void;
  preSave: () => Promise<void>;

  // Computed values
  getOptimizedCampaigns: () => CampaignBudgetSettings[]; // Returns campaigns with optimization enabled AND status ENABLED
  getOptimizedButPausedCampaigns: () => CampaignBudgetSettings[]; // Returns campaigns with optimization enabled but NOT ENABLED (e.g., PAUSED)
  getAllOptimizationEnabledCampaigns: () => CampaignBudgetSettings[]; // Returns all campaigns with optimization flag true (regardless of status)
  updateCampaignStatus: (campaignId: string, status: string) => void;
  getTotalCurrentBudget: () => number;
  getTotalMinimumAllocations: () => number;
  validateBudgetConfiguration: () => { isValid: boolean; errors: string[] };
}

const DEFAULT_CONFIG: BudgetOptimizationConfig = {
  hasConversionTracking: false,
  hasRevenuePerConversionTracking: false,
  revenuePerImpression: null,
  revenuePerConversion: null,
  dowWise: false,
  devImplementOptBudgets: false,
};

export const useCampaignSettingsGoogleAdsStore = create<BudgetOptimizerStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      config: DEFAULT_CONFIG,
      campaignSettings: new Map(),
      isOptimizing: false,
      optimizationResults: null,
      error: null,
      manualBudgetChanges: new Set(),
      optimizationToggleChanges: new Set(),
      manualNameChanges: new Set(),

      // Actions
      setConfig: (newConfig: Partial<BudgetOptimizationConfig>) => {
        const { config: currentConfig } = get();
        set({ config: { ...currentConfig, ...newConfig } });
        debug.log('BudgetOptimizer: Config updated', newConfig);
      },

      setCampaignSettings: (campaignId: string, settings: Partial<CampaignBudgetSettings>) => {
        const campaignSettings = new Map(get().campaignSettings);
        const currentSettings = campaignSettings.get(campaignId) || {
          campaignId,
          isOptimizationEnabled: false,
          originalOptimizationEnabled: false,
          minimumAllocation: 1,
          manualBudget: null,
          currentBudget: 0,
          manualName: null,
          currentName: '',
          googleAdsAccountId: null,
          nameValidationError: null,
          campaignStatus: 'UNKNOWN',
          originalCampaignStatus: 'UNKNOWN',
        };
        campaignSettings.set(campaignId, { ...currentSettings, ...settings });
        set({ campaignSettings });
      },

      toggleCampaignOptimization: (campaignId: string, enabled: boolean) => {
        const { setCampaignSettings, optimizationToggleChanges, campaignSettings } = get();
        setCampaignSettings(campaignId, { isOptimizationEnabled: enabled });

        // Get the original value to compare
        const settings = campaignSettings.get(campaignId);
        const originalValue = settings?.originalOptimizationEnabled ?? false;

        // Track as changed only if different from original
        const updatedToggleChanges = new Set(optimizationToggleChanges);
        if (enabled !== originalValue) {
          updatedToggleChanges.add(campaignId);
        } else {
          // Back to original state - remove from pending changes
          updatedToggleChanges.delete(campaignId);
        }
        set({ optimizationToggleChanges: updatedToggleChanges });

        // Target density will be recalculated by the component effect
        // when campaigns change
      },

      setManualBudget: (campaignId: string, budget: number) => {
        const { setCampaignSettings, manualBudgetChanges, campaignSettings } = get();
        setCampaignSettings(campaignId, { manualBudget: budget });

        const updatedChanges = new Set(manualBudgetChanges);
        const settings = campaignSettings.get(campaignId);
        const originalBudget = settings?.currentBudget || 0;

        // Only mark as changed if the budget is actually different from original
        if (Math.abs(budget - originalBudget) > 0.001) { // Use small epsilon for floating point comparison
          updatedChanges.add(campaignId);
        } else {
          updatedChanges.delete(campaignId);
        }

        set({ manualBudgetChanges: updatedChanges });
      },

      setManualName: (campaignId: string, name: string) => {
        const { setCampaignSettings, manualNameChanges, campaignSettings } = get();

        // Validate the campaign name against Google Ads API requirements
        const validation = validateCampaignName(name);

        // Always save the trimmed name, even if invalid (for user feedback)
        // But store the validation error for UI to display
        setCampaignSettings(campaignId, {
          manualName: validation.sanitizedName,
          nameValidationError: validation.error
        });

        const updatedChanges = new Set(manualNameChanges);
        const settings = campaignSettings.get(campaignId);
        const originalName = (settings?.currentName || '').trim();

        // Only mark as changed if the name is valid and different from original
        // Compare trimmed values to handle leading/trailing whitespace
        if (validation.isValid && validation.sanitizedName !== originalName) {
          updatedChanges.add(campaignId);
        } else {
          updatedChanges.delete(campaignId);
        }

        set({ manualNameChanges: updatedChanges });

        // Log validation errors for debugging
        if (!validation.isValid) {
          debug.log('Campaign name validation failed', {
            campaignId,
            name,
            error: validation.error
          });
        }
      },

      setMinimumAllocation: (campaignId: string, percentage: number) => {
        const { setCampaignSettings } = get();
        setCampaignSettings(campaignId, { minimumAllocation: percentage });
      },

      initializeCampaignSettings: (campaigns: Campaign[]) => {
        // Merge with existing settings rather than rebuilding from scratch.
        // The Account page re-runs the consumer's useEffect whenever the
        // `campaigns` array reference changes — which happens on every
        // optimistic status toggle, budget refetch, etc. A naive rebuild
        // clobbers the user's pending flower-toggle / manual-budget /
        // manual-name edits (those live only in this store until save),
        // making the Allocatable Budget card vanish mid-session when an
        // adjacent campaign's status changes. Preserve user-staged fields
        // for any campaign that's in the corresponding change-tracking
        // Set; refresh server-side fields (`currentBudget`, `currentName`,
        // `campaignStatus`, `originalOptimizationEnabled`) from the new
        // data so re-baselining still works.
        const {
          campaignSettings: existing,
          optimizationToggleChanges,
          manualBudgetChanges,
          manualNameChanges,
        } = get();
        const campaignSettings = new Map<string, CampaignBudgetSettings>();
        campaigns.forEach(campaign => {
          const optimizationEnabled = campaign.flouri_optimize_budget === true;
          const prior = existing.get(campaign.campaign_id);
          const hasPendingToggle =
            prior !== undefined && optimizationToggleChanges.has(campaign.campaign_id);
          const hasPendingBudget =
            prior !== undefined && manualBudgetChanges.has(campaign.campaign_id);
          const hasPendingName =
            prior !== undefined && manualNameChanges.has(campaign.campaign_id);
          const serverStatus = campaign.status || 'UNKNOWN';
          // Preserve in-flight optimistic status from a pause/enable
          // that hasn't been saved/discarded yet — same shape as
          // hasPendingToggle. Without this, a refetch mid-edit would
          // clobber the optimistic value.
          const hasPendingStatus =
            prior !== undefined && prior.campaignStatus !== prior.originalCampaignStatus;
          campaignSettings.set(campaign.campaign_id, {
            campaignId: campaign.campaign_id,
            isOptimizationEnabled: hasPendingToggle
              ? prior!.isOptimizationEnabled
              : optimizationEnabled,
            originalOptimizationEnabled: optimizationEnabled, // always reflects latest server baseline
            minimumAllocation: prior?.minimumAllocation ?? 1,
            manualBudget: hasPendingBudget ? prior!.manualBudget : null,
            currentBudget: campaign.budget || 0,
            manualName: hasPendingName ? prior!.manualName : null,
            currentName: campaign.campaign_name || '',
            googleAdsAccountId: campaign.google_ads_account_id || null,
            nameValidationError: hasPendingName ? prior!.nameValidationError : null,
            campaignStatus: hasPendingStatus ? prior!.campaignStatus : serverStatus,
            originalCampaignStatus: serverStatus, // always reflects latest server baseline
          });
        });
        // Idempotency: skip the `set()` call (and the subscriber
        // notification cascade) when the just-built Map is value-equal
        // to what's already in state. Mirrors the Meta store's
        // identical guard (shipped 735ae278) — closes the same
        // potential refetch-identical-data feedback loop on the
        // Google side that the Meta-side fix closed.
        const existingMap = existing;
        let differs = existingMap.size !== campaignSettings.size;
        if (!differs) {
          for (const [id, next] of campaignSettings) {
            const prev = existingMap.get(id);
            if (
              !prev ||
              prev.campaignId !== next.campaignId ||
              prev.isOptimizationEnabled !== next.isOptimizationEnabled ||
              prev.originalOptimizationEnabled !== next.originalOptimizationEnabled ||
              prev.minimumAllocation !== next.minimumAllocation ||
              prev.manualBudget !== next.manualBudget ||
              prev.currentBudget !== next.currentBudget ||
              prev.manualName !== next.manualName ||
              prev.currentName !== next.currentName ||
              prev.googleAdsAccountId !== next.googleAdsAccountId ||
              prev.nameValidationError !== next.nameValidationError ||
              prev.campaignStatus !== next.campaignStatus ||
              prev.originalCampaignStatus !== next.originalCampaignStatus
            ) {
              differs = true;
              break;
            }
          }
        }
        if (differs) {
          set({ campaignSettings });
          debug.log('BudgetOptimizer: Initialized campaign settings', campaigns.length);
        }
      },

      setOptimizationResults: (results: BudgetAllocationResult[] | null) => {
        set({ optimizationResults: results });
      },

      setOptimizing: (isOptimizing: boolean) => {
        set({ isOptimizing });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      preSave: async () => {
        // Google-specific pre-save side effects: flush per-campaign manual
        // budget and name edits via dedicated optimizationApi endpoints
        // separate from the app_config write. Meta has no analogous
        // endpoints — its rename flushes through pendingChangesStore →
        // metaAdsApi.batchUpdate outside this codepath.
        const { campaignSettings, manualBudgetChanges, manualNameChanges } = get();

        if (manualNameChanges.size > 0) {
          const { optimizationApi } = await import('@/services/api/optimization');
          const manualNameUpdates: Record<string, string> = {};
          manualNameChanges.forEach((campaignId) => {
            const settings = campaignSettings.get(campaignId);
            if (settings?.manualName !== null && settings?.manualName !== undefined) {
              manualNameUpdates[campaignId] = settings.manualName;
            }
          });
          if (Object.keys(manualNameUpdates).length > 0) {
            debug.log('Attempting manual name update with data:', { campaign_name_updates: manualNameUpdates });
            await optimizationApi.updateManualNames({ campaign_name_updates: manualNameUpdates });
            debug.log('Manual name update successful');
          }
        }

        if (manualBudgetChanges.size > 0) {
          const { optimizationApi } = await import('@/services/api/optimization');
          const manualBudgetUpdates: Record<string, number> = {};
          manualBudgetChanges.forEach((campaignId) => {
            const settings = campaignSettings.get(campaignId);
            if (settings?.manualBudget !== null && settings?.manualBudget !== undefined) {
              manualBudgetUpdates[campaignId] = settings.manualBudget;
            }
          });
          if (Object.keys(manualBudgetUpdates).length > 0) {
            debug.log('Attempting manual budget update with data:', { campaign_budget_updates: manualBudgetUpdates });
            await optimizationApi.updateManualBudgets({ campaign_budget_updates: manualBudgetUpdates });
            debug.log('Manual budget update successful');
          }
        }
      },

      toConfigSlice: (existing) => {
        const { campaignSettings, manualNameChanges } = get();
        // Per-campaign target_density mirrors the account-level setting now
        // that the field lives on accountSettingsStore.
        const accountTargetDensity = useAccountSettingsStore.getState().targetDensity;

        // Google's slice contributes ONLY Google entries. Every platform
        // store's slice/hydrate is an inclusion filter on its own
        // platform_source — that pattern scales when TikTok / future
        // platforms land (each registers a slice that filters in its own
        // platform_source). Pre-A3 entries lack platform_source; backend
        // helpers default missing → 'Google Ads', so the frontend matches.
        const existingCampaignList = existing?.campaign_list || [];
        const existingCampaignsMap = new Map<string, AppCampaignConfig>();
        existingCampaignList.forEach((campaign) => {
          if ((campaign.platform_source ?? 'Google Ads') !== 'Google Ads') return;
          existingCampaignsMap.set(String(campaign.campaign_id), campaign);
        });

        // Only fall through to settings.manualName when the name passed
        // validation and was tracked as a real change. setManualName writes
        // to manualName even when validation fails (so the UI can keep
        // showing what the user typed alongside the error), but the failed
        // name must not leak into the saved configuration blob — otherwise
        // it becomes currentName on the next load.
        const safeManualName = (campaignId: string, settings: CampaignBudgetSettings): string | null =>
          manualNameChanges.has(campaignId) && settings.nameValidationError === null
            ? settings.manualName
            : null;

        const updatedCampaignList: AppCampaignConfig[] = [];

        existingCampaignsMap.forEach((existingCampaign, campaignId) => {
          const settings = campaignSettings.get(campaignId);
          if (settings) {
            updatedCampaignList.push({
              ...existingCampaign,
              campaign_name: safeManualName(campaignId, settings) || settings.currentName || existingCampaign.campaign_name,
              budget: settings.manualBudget ?? settings.currentBudget ?? existingCampaign.budget,
              target_density: accountTargetDensity,
              flouri_optimize_budget: settings.isOptimizationEnabled,
              google_ads_account_id: settings.googleAdsAccountId || existingCampaign.google_ads_account_id,
              // `campaign_status` (NOT `status`) is the canonical campaign-level field
              // in the saved-config blob — matches what the backend writes in
              // `_update_campaign_status_in_config` (campaigns.py),
              // `account_configuration.py`, and Meta's equivalent in
              // meta_campaigns.py. A stale top-level `status` would shadow backend
              // updates on read. `meaningfulStatus` guards against overwriting a
              // valid `existingCampaign.campaign_status` with a sentinel ('UNKNOWN'
              // / 'UNSPECIFIED') that `initializeCampaignSettings` set when the
              // API returned no status.
              campaign_status: meaningfulStatus(settings.campaignStatus) || existingCampaign.campaign_status,
            });
          } else {
            updatedCampaignList.push(existingCampaign);
          }
        });

        campaignSettings.forEach((settings, campaignId) => {
          if (!existingCampaignsMap.has(campaignId)) {
            updatedCampaignList.push({
              campaign_id: campaignId,
              campaign_name: safeManualName(campaignId, settings) || settings.currentName,
              budget: settings.manualBudget ?? settings.currentBudget ?? 0,
              target_density: accountTargetDensity,
              flouri_optimize_budget: settings.isOptimizationEnabled,
              google_ads_account_id: settings.googleAdsAccountId || undefined,
              // Omit campaign_status entirely if it's a sentinel so the backend
              // doesn't see a sentinel value as saved-config source of truth.
              campaign_status: meaningfulStatus(settings.campaignStatus),
              platform_source: 'Google Ads',
            });
          }
        });

        return { campaign_list: updatedCampaignList };
      },

      hydrateFromConfig: (config) => {
        if (!config?.campaign_list || !Array.isArray(config.campaign_list)) return;
        const { campaignSettings } = get();
        const updatedCampaignSettings = new Map(campaignSettings);
        config.campaign_list.forEach((savedCampaign) => {
          // Inclusion filter on platform_source — keeps Google entries only,
          // ignoring Meta and any future platform. Pre-A3 entries lack the
          // stamp; backend helpers default missing → 'Google Ads', so the
          // frontend matches. Mirrors the symmetric filter in
          // campaignSettingsMetaAdsStore.hydrateFromConfig.
          if ((savedCampaign.platform_source ?? 'Google Ads') !== 'Google Ads') return;

          const id = String(savedCampaign.campaign_id);
          const existingSettings = updatedCampaignSettings.get(id);
          if (existingSettings) {
            const optimizationEnabled = savedCampaign.flouri_optimize_budget === true;
            // Saved-config status is the discard baseline going forward
            // (most recent server-confirmed value). Falls back to the
            // existing prior value when saved is missing/sentinel.
            // `meaningfulStatus` is the same guard used in `toConfigSlice`
            // + the Meta store's hydrate.
            const savedStatus = meaningfulStatus(savedCampaign.campaign_status)
              || meaningfulStatus(savedCampaign.status)
              || existingSettings.campaignStatus;
            updatedCampaignSettings.set(id, {
              ...existingSettings,
              isOptimizationEnabled: optimizationEnabled,
              originalOptimizationEnabled: optimizationEnabled,
              campaignStatus: savedStatus,
              originalCampaignStatus: savedStatus,
              // `??` not `||` for budget — `0` is a valid value (paused
              // campaign / explicit zero budget); `||` would treat it as
              // missing and silently preserve the prior currentBudget.
              currentBudget: savedCampaign.budget ?? existingSettings.currentBudget,
              // `||` (not `??`) for the string fields: empty string from
              // saved config is degenerate data we'd rightly fall back from.
              currentName: savedCampaign.campaign_name || existingSettings.currentName,
              googleAdsAccountId: savedCampaign.google_ads_account_id || existingSettings.googleAdsAccountId,
              manualBudget: null,
              manualName: null,
            });
          }
        });
        set({ campaignSettings: updatedCampaignSettings });
        debug.log('CampaignSettings: hydrated campaign_list from saved config');
      },

      markSaved: () => {
        set({
          manualBudgetChanges: new Set(),
          manualNameChanges: new Set(),
          optimizationToggleChanges: new Set(),
        });
      },

      discardChanges: () => {
        const { campaignSettings } = get();

        // Reset all campaign settings to their original values
        const resetSettings = new Map(campaignSettings);
        resetSettings.forEach((settings, campaignId) => {
          resetSettings.set(campaignId, {
            ...settings,
            // Reset optimization to original value
            isOptimizationEnabled: settings.originalOptimizationEnabled,
            // Reset status to original value — without this, a user-paused
            // (then discarded) campaign stays stuck on the optimistic
            // PAUSED in `campaignSettings`, which `getOptimizedCampaigns`
            // filters out → the campaign disappears from the pool until
            // the next refetch. Symmetric Meta-store fix shipped
            // alongside.
            campaignStatus: settings.originalCampaignStatus,
            // Clear manual edits
            manualBudget: null,
            manualName: null,
            nameValidationError: null,
          });
        });

        set({
          campaignSettings: resetSettings,
          manualBudgetChanges: new Set(),
          manualNameChanges: new Set(),
          optimizationToggleChanges: new Set(),
        });

        debug.log('BudgetOptimizer: Discarded all pending changes (per-campaign only)');
      },

      // Update campaign status (called when user enables/pauses a campaign)
      updateCampaignStatus: (campaignId: string, status: string) => {
        const { setCampaignSettings } = get();
        setCampaignSettings(campaignId, { campaignStatus: status });
        debug.log('BudgetOptimizer: Updated campaign status', { campaignId, status });
      },

      // Computed values
      // Returns campaigns with optimization enabled AND status ENABLED (for budget allocation)
      getOptimizedCampaigns: () => {
        const { campaignSettings } = get();
        return Array.from(campaignSettings.values()).filter(
          settings => settings.isOptimizationEnabled && settings.campaignStatus === 'ENABLED'
        );
      },

      // Returns campaigns with optimization enabled but NOT ENABLED status (for UI display)
      getOptimizedButPausedCampaigns: () => {
        const { campaignSettings } = get();
        return Array.from(campaignSettings.values()).filter(
          settings => settings.isOptimizationEnabled && settings.campaignStatus !== 'ENABLED'
        );
      },

      // Returns all campaigns with optimization flag true (regardless of status)
      getAllOptimizationEnabledCampaigns: () => {
        const { campaignSettings } = get();
        return Array.from(campaignSettings.values()).filter(
          settings => settings.isOptimizationEnabled
        );
      },

      getTotalCurrentBudget: () => {
        const optimizedCampaigns = get().getOptimizedCampaigns();
        // Use manualBudget when the user has staged an edit, otherwise fall back to
        // the server-baseline currentBudget. Mirrors the coalescing in toConfigSlice
        // so the displayed total + auto-sync target reflect what would actually be saved.
        const total = optimizedCampaigns.reduce(
          (sum, campaign) => sum + (campaign.manualBudget ?? campaign.currentBudget),
          0
        );
        // Round to cents to avoid floating point precision issues
        return Math.round(total * 100) / 100;
      },

      getTotalMinimumAllocations: () => {
        const optimizedCampaigns = get().getOptimizedCampaigns();
        return optimizedCampaigns.reduce((sum, campaign) => sum + campaign.minimumAllocation, 0);
      },

      validateBudgetConfiguration: () => {
        const { getTotalMinimumAllocations } = get();
        const errors: string[] = [];

        // Check if total budget is positive (lives on accountSettingsStore now)
        if (useAccountSettingsStore.getState().totalBudget <= 0) {
          errors.push('Total budget must be greater than zero');
        }

        // Check if minimum allocations exceed 100%
        const totalMinimumAllocations = getTotalMinimumAllocations();
        if (totalMinimumAllocations > 100) {
          errors.push(
            `Total minimum allocations (${totalMinimumAllocations.toFixed(1)}%) exceed 100%`
          );
        }

        // Check if any optimized campaigns are selected
        const optimizedCampaigns = get().getOptimizedCampaigns();
        if (optimizedCampaigns.length === 0) {
          errors.push('Please select at least one campaign for budget optimization');
        }

        return {
          isValid: errors.length === 0,
          errors
        };
      },
    }),
    {
      name: 'campaign-settings-google-ads-store',
    }
  )
);